<template>
  <div>
    <!-- Media -->
    <b-media class="mb-2">
      <template #aside>
        <b-avatar
            ref="previewEl"
            :src="projectLocal.avatar"
            :text="avatarText(projectLocal.project_code)"
            :variant="`light-${resolveProjectTypeVariant(projectLocal.project_code)}`"
            rounded
            size="90px"
        />
      </template>
      <h4 class="d-flex align-items-center mb-1">
        {{ projectLocal.project_code }}
      </h4>
    </b-media>
    <hr/>
    <!-- Project Info: Input Fields -->
    <b-form @submit.prevent="onSubmit" @reset.prevent="resetForm">
      <b-row class="my-2">
        <b-col cols="12">
          <span class="font-weight-bold">Informations Générales</span>
        </b-col>
      </b-row>
      <b-row class="mb-2">
        <!-- Field: Code -->
        <b-col cols="12" md="4">
          <b-form-group label="Code" label-for="code">
            <b-form-input id="code" v-model="projectLocal.project_code" disabled/>
          </b-form-group>
        </b-col>

        <!-- Field: Customer -->
        <b-col cols="12" md="4">
          <b-form-group label="Client" label-for="customer">
            <v-select
                v-model="projectLocal.customer"
                :clearable="false"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="customers"
                :reduce="customer => customer.id"
                :taggable="false"
                input-id="customer"
                label="name"
                placeholder="Choisir un client existant"
            >
              <template #no-options>Aucun client trouvé.</template>
            </v-select>
          </b-form-group>
        </b-col>

        <!-- Field: Lessor -->
        <b-col cols="12" md="4">
          <b-form-group label="Bailleur" label-for="lessor">
            <b-form-input id="lessor" v-model="projectLocal.details.lessor"/>
          </b-form-group>
        </b-col>

        <!-- Field: Type -->
        <b-col cols="12" md="4">
          <b-form-group label="Type" label-for="project-type">
            <v-select
                v-model="projectLocal.type"
                :clearable="false"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="typeOptions"
                :reduce="(type) => type.value"
                input-id="project-type"
            />
          </b-form-group>
        </b-col>

        <!-- Field: Concerned Department -->
        <b-col cols="12" md="4">
          <b-form-group
              label="Département Concerné"
              label-for="concerned_department"
          >
            <department-select v-model="projectLocal.details.concerned_department" :departments="departmentsOptions"/>
          </b-form-group>
        </b-col>

        <!-- Field: Deposit Date -->
        <b-col cols="12" md="4">
          <b-form-group label="Date de Dépôt" label-for="deposit_date">
            <flat-pickr
                id="deposit_date"
                v-model="projectLocal.details.deposit_date"
                class="form-control"
            />
          </b-form-group>
        </b-col>

        <!-- Field: Deposit Place -->
        <b-col cols="12" md="4">
          <b-form-group label="Lieu de Dépôt" label-for="deposit_place">
            <b-form-input
                id="deposit_place"
                v-model="projectLocal.details.deposit_place"
            />
          </b-form-group>
        </b-col>

        <!-- Field: Brief -->
        <b-col cols="12" md="8">
          <b-form-group label="Description" label-for="project-description">
            <quill-editor
                id="quil-content"
                v-model="projectLocal.brief"
                :options="editorOption"
                class="border-bottom-0"
            />
            <div
                id="quill-toolbar"
                class="d-flex justify-content-end border-top-0"
            >
              <!-- Add a bold button -->
              <button class="ql-bold"/>
              <button class="ql-italic"/>
              <button class="ql-underline"/>
              <button class="ql-align"/>
              <button class="ql-link"/>
            </div>
          </b-form-group>
        </b-col>
      </b-row>

      <hr/>

      <!-- Action Buttons -->
      <b-button
          :block="$store.getters['app/currentBreakPoint'] === 'xs'"
          :disabled="isLoading"
          class="mb-1 mb-sm-0 mr-0 mr-sm-1"
          type="submit"
          variant="primary"
      >
        <b-spinner v-if="isLoading" small></b-spinner>
        Sauvegarder
      </b-button>
      <b-button
          :block="$store.getters['app/currentBreakPoint'] === 'xs'"
          variant="outline-secondary"
          @click="() => $router.go(-1)"
      >
        Retour
      </b-button>
    </b-form>
  </div>
</template>

<script>
import DepartmentSelect from "@/components/departments/DepartmentSelect.vue";
import {BAvatar, BButton, BCol, BForm, BFormGroup, BFormInput, BMedia, BRow, BSpinner,} from "bootstrap-vue";
import flatPickr from "vue-flatpickr-component";
import {quillEditor} from "vue-quill-editor";
import {avatarText} from "@core/utils/filter";
import vSelect from "vue-select";
import {useInputImageRenderer} from "@core/comp-functions/forms/form-utils";
import {onMounted, ref, watch} from "@vue/composition-api";
import useProjectsList from "../projects-list/useProjectsList";
import store from "@/store";
import {useRouter} from "@core/utils/utils";
import {typeOptions} from "@/constants/bid";

export default {
  components: {
    DepartmentSelect,
    BButton,
    BMedia,
    BAvatar,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BForm,
    BSpinner,
    vSelect,
    flatPickr,
    quillEditor,
  },
  props: {
    projectData: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const {router} = useRouter();
    const isLoading = ref(false);

    onMounted(() => {
      let props = ["customers", "departments", "countries"];
      fetchProjectProperties(props);
    });

    const {
      fetchProjectProperties,
      resolveProjectTypeVariant,
      customerOptions,
      countryOptions,
      departmentsOptions,
    } = useProjectsList();

    const editorOption = {
      modules: {
        toolbar: "#quill-toolbar",
      },
      placeholder: "Écrivez une description brieve du projet",
    };

    // ? Demo Purpose => Update image on click of update
    const refInputEl = ref(null);
    const previewEl = ref(null);

    const {inputImageRenderer} = useInputImageRenderer(
        refInputEl,
        (base64) => {
          // eslint-disable-next-line no-param-reassign
          props.projectData.avatar = base64;
        }
    );

    const projectLocal = ref(JSON.parse(JSON.stringify(props.projectData)));

    const resetForm = () => {
      projectLocal.value = JSON.parse(JSON.stringify(props.projectData));
    };

    watch(
        projectLocal,
        (newProjectData) => {
          if (newProjectData !== undefined) {
            if (
                newProjectData.hasOwnProperty("customer") &&
                typeof newProjectData.customer === "object"
            ) {
              newProjectData.customer = newProjectData.customer?.id;
            }
          }
        },
        {
          deep: true,
          immediate: true,
        }
    );

    watch(
        props.projectData,
        () => {
          resetForm();
        },
        {deep: true}
    );

    const onSubmit = () => {
      isLoading.value = true;

      store
          .dispatch("project/updateProject", projectLocal.value)
          .then((response) => {
            projectLocal.value = response.resource;
            isLoading.value = false;
            router.go(-1);
          })
          .catch(() => {
            isLoading.value = false
          });
    };

    return {
      isLoading,
      resolveProjectTypeVariant,
      avatarText,

      typeOptions,
      countryOptions,
      departmentsOptions,

      customers: customerOptions,
      projectLocal,

      resetForm,
      onSubmit,

      // UI
      editorOption,

      //  ? Demo - Update Image on click of update button
      refInputEl,
      previewEl,
      inputImageRenderer,
    };
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/map-leaflet.scss";
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
@import "@core/scss/vue/libs/quill.scss";
</style>

<style lang="scss" scoped>
@import "~@core/scss/base/bootstrap-extended/include";

.assignee-selector {
  ::v-deep .vs__dropdown-toggle {
    padding-left: 0;
  }
}

#quil-content ::v-deep {
  > .ql-container {
    border-bottom: 0;
  }

  + #quill-toolbar {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    border-bottom-left-radius: $border-radius;
    border-bottom-right-radius: $border-radius;
  }
}
</style>
